import React from "react";
import { I } from "./icons";

// ─────────────── Sub-tab strip (reused for inner nav) ───────────────
function TabStrip({ tabs, active, setActive }) {
  return (
    <div className="wb-tabs" style={{flexWrap:'wrap'}}>
      {tabs.map((t, i) => (
        <button key={t.k} className={"wb-tab" + (active === i ? " active" : "")} onClick={() => setActive(i)}>
          <span className="num">{i+1}</span> {t.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────── Breakpoint hook ───────────────
// Mirrors the @media (max-width:1100px) rule in workbench.css that switches
// between the desktop and mobile stages. Surfaces mount ONLY the active
// breakpoint's chrome — mounting both and hiding one with CSS (the prototype's
// approach) kept two live copies of each screen whose local state could
// silently drift apart (see docs/design-handoff/PARKED_WORK.md §3).
const MOBILE_MQ = "(max-width: 1100px)";
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.matchMedia(MOBILE_MQ).matches);
  React.useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

// ─────────────── Device chrome helpers ───────────────
function DesktopBrowser({ url = "carecardexpress.com / send", children, width }) {
  return (
    <div className="browser" style={width ? { width } : undefined}>
      <div className="browser-chrome">
        <div className="traffic"><span/><span/><span/></div>
        <div className="url-bar">
          <span className="lock"><I.Lock size={10} stroke={2}/></span>
          {url}
        </div>
        <div style={{width: 40}}/>
      </div>
      <div className="browser-body">
        {children}
      </div>
    </div>
  );
}

function iosTime() {
  const d = new Date();
  let h = d.getHours(); const m = d.getMinutes();
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2,'0')}`;
}

function IOSFrame({ children }) {
  return (
    <div className="ios-shell">
      <div className="ios-screen">
        <div className="ios-notch"/>
        <div className="ios-status">
          <span>{iosTime()}</span>
          <span className="icons">
            <svg width="17" height="10" viewBox="0 0 17 10">
              <rect x="0" y="6" width="3" height="4" rx="0.6" fill="currentColor"/>
              <rect x="4.5" y="4" width="3" height="6" rx="0.6" fill="currentColor"/>
              <rect x="9" y="2" width="3" height="8" rx="0.6" fill="currentColor"/>
              <rect x="13.5" y="0" width="3" height="10" rx="0.6" fill="currentColor"/>
            </svg>
            <svg width="25" height="11" viewBox="0 0 25 11">
              <rect x="0.5" y="0.5" width="22" height="10" rx="3" stroke="currentColor" fill="none" opacity="0.4"/>
              <rect x="2" y="2" width="19" height="7" rx="1.5" fill="currentColor"/>
              <rect x="23" y="3.5" width="1.5" height="4" rx="0.5" fill="currentColor" opacity="0.6"/>
            </svg>
          </span>
        </div>
        <div className="ios-scroll">{children}</div>
        <div className="ios-home"/>
      </div>
    </div>
  );
}

export { TabStrip, DesktopBrowser, IOSFrame, iosTime, useIsMobile };

import { BrandMark } from "./brand";
import { useTheme } from "./theme";
import {
  useAccentColor,
  useTextSize,
  useHighContrast,
  useOptionalProfile,
  ACCENTS,
  TEXT_SIZES,
  RouteAnnouncer,
} from "./prefs";
import { useSiteLocale, SITE_LOCALES } from "./flow_copy";
import "./chrome.css";

// ─────────────── Global site chrome ───────────────
// <SiteHeader/> + <SiteFooter/> render on EVERY customer page. The
// coordinator mounts them around each route (outside <main id="cc-main">).
// Everything here is router-agnostic (plain <a> links) so the same
// components render in the live site and in the design workbench.
//
// Proposed routes (coordinator wires these in site.jsx):
//   /send        Send a card (order flow)
//   /mail-guide  MailGuidePage  (info_pages.jsx)
//   /services    ServicesPage   (info_pages.jsx)
//   /faq         FAQPage        (info_pages.jsx)
//   /lookup      OrderLookup    (guest_extras.jsx)
//   /about /contact /security /terms /privacy  (existing pages)

export { RouteAnnouncer };

function SkipLink() {
  const { t } = useSiteLocale();
  return (
    <a className="cc-skip-link" href="#cc-main">
      {t.skipToContent}
    </a>
  );
}

/** EN/ES/HT language selector. Same key + broadcast as the order flow, so one
 *  choice applies everywhere; <html lang> updates via useFlowLocale. */
function SiteLanguageSwitcher({ compact = false }) {
  const { locale, setLocale, t } = useSiteLocale();
  const labels = { en: "EN", es: "ES", ht: "HT" };
  const names = { en: "English", es: "Español", ht: "Kreyòl" };
  return (
    <div className="cc-control-group">
      {!compact && <span className="cc-control-label" id="cc-lang-label">{t.languageLabel}</span>}
      <div className="cc-seg" role="group" aria-labelledby={compact ? undefined : "cc-lang-label"} aria-label={compact ? t.languageLabel : undefined}>
        {SITE_LOCALES.map((id) => (
          <button
            key={id}
            type="button"
            aria-pressed={locale === id}
            aria-label={names[id]}
            title={names[id]}
            onClick={() => setLocale(id)}
          >
            {labels[id]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** BUILD-20: visible, guest-accessible EN/ES/HT language switcher as an
 *  accessible NATIVE control — a <select> with a <label> in the currently
 *  active language. Same storage key + broadcast as the button-group
 *  switcher, so the two never disagree. Used in the global header, the
 *  footer, and account settings. Switching applies at runtime, persists
 *  local-first (no account required), and never unmounts the send flow, so
 *  in-progress draft state survives a language change. */
function LanguageSelect({ id = "cc-language-select" }) {
  const { locale, setLocale, t } = useSiteLocale();
  const names = { en: "English", es: "Español", ht: "Kreyòl" };
  return (
    <div className="cc-lang-select">
      <label htmlFor={id}>{t.languageLabel}</label>
      <select
        id={id}
        value={locale}
        onChange={(e) => setLocale(e.target.value)}
      >
        {SITE_LOCALES.map((l) => (
          <option key={l} value={l}>{names[l]}</option>
        ))}
      </select>
    </div>
  );
}

/** §4.2 theme control: stable switch semantics. The accessible name is
 *  "Light theme" in the current language (t.themeSwitchName) and
 *  aria-checked=true means light mode is enabled. The visible on/off state
 *  changes; the semantic control name does not. */
function ThemeSwitch({ id = "cc-theme-switch" }) {
  const [mode, setMode] = useTheme();
  const { t } = useSiteLocale();
  const light = mode === "light";
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={light}
      aria-label={t.themeSwitchName}
      className="cc-theme-switch"
      data-on={light ? "true" : "false"}
      onClick={() => setMode(light ? "dark" : "light")}
    >
      <span className="cc-theme-switch-track" aria-hidden="true">
        <span className="cc-theme-switch-thumb" />
      </span>
      <span className="cc-theme-switch-label">{t.themeSwitchName}</span>
    </button>
  );
}

/** Light / Dark / System — shares the cc-theme key with theme.js, so the
 *  workbench toggle and the site header never disagree. */
function SiteThemeControls({ compact = false }) {
  const [mode, setMode] = useTheme();
  const { t } = useSiteLocale();
  const options = [
    { id: "light", label: t.themeLight },
    { id: "dark", label: t.themeDark },
    { id: "system", label: t.themeSystem },
  ];
  return (
    <div className="cc-control-group">
      {!compact && <span className="cc-control-label" id="cc-theme-label">{t.themeLabel}</span>}
      <div className="cc-seg" role="group" aria-labelledby={compact ? undefined : "cc-theme-label"} aria-label={compact ? t.themeLabel : undefined}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            aria-pressed={mode === o.id}
            onClick={() => setMode(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Accent color switcher — Gold (default), Rose, Emerald. Black base always;
 *  navy/blue are never offered. Persists in localStorage (cc-accent-v1). */
function SiteAccentSwitcher({ compact = false }) {
  const [accent, setAccent] = useAccentColor();
  const { t } = useSiteLocale();
  const names = { gold: t.accentGold, rose: t.accentRose, emerald: t.accentEmerald };
  const hex = { gold: "#FFC107", rose: "#FB7185", emerald: "#34D399" };
  return (
    <div className="cc-control-group">
      {!compact && <span className="cc-control-label" id="cc-accent-label">{t.accentLabel}</span>}
      <div className="cc-swatches" role="group" aria-labelledby={compact ? undefined : "cc-accent-label"} aria-label={compact ? t.accentLabel : undefined}>
        {ACCENTS.map((id) => (
          <button
            key={id}
            type="button"
            className="cc-swatch"
            aria-pressed={accent === id}
            aria-label={names[id]}
            title={names[id]}
            onClick={() => setAccent(id)}
          >
            <span style={{ background: hex[id] }} aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}

/** Accessibility controls: 3 text sizes + high-contrast toggle. Lives in a
 *  disclosure so the header stays compact; keyboard and touch friendly. */
function A11yControls() {
  const [size, setSize] = useTextSize();
  const [contrast, setContrast] = useHighContrast();
  const { t } = useSiteLocale();
  const sizeNames = { standard: t.textSizeStandard, large: t.textSizeLarge, xlarge: t.textSizeXLarge };
  return (
    <div className="cc-a11y">
      <details>
        <summary aria-label={t.a11yLabel}>{t.a11yLabel}</summary>
        <div className="cc-a11y-panel">
          <div className="cc-a11y-row">
            <span className="cc-control-label" id="cc-ts-label">{t.textSizeLabel}</span>
            <div className="cc-seg" role="group" aria-labelledby="cc-ts-label">
              {TEXT_SIZES.map((id) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={size === id}
                  onClick={() => setSize(id)}
                >
                  {sizeNames[id]}
                </button>
              ))}
            </div>
          </div>
          <div className="cc-a11y-row">
            <span className="cc-control-label" id="cc-contrast-label">{t.contrastLabel}</span>
            <button
              type="button"
              className="cc-a11y-toggle"
              aria-pressed={contrast}
              aria-labelledby="cc-contrast-label"
              onClick={() => setContrast(!contrast)}
            >
              {contrast ? t.contrastOn : t.contrastOff}
            </button>
          </div>
        </div>
      </details>
    </div>
  );
}

/** Optional sign-in entry point. There is deliberately NO auth backend:
 *  this stores an optional name + email on this device only (localStorage),
 *  so the order form can prefill. It is labeled optional everywhere and
 *  guest checkout never depends on it. */
function OptionalProfile() {
  const { t } = useSiteLocale();
  const { profile, save, clear } = useOptionalProfile();
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");

  if (profile && !open) {
    return (
      <div className="cc-profile">
        <button type="button" className="cc-profile-btn" onClick={() => setOpen(true)}
          aria-label={`${t.profileSavedAs(profile.name || profile.email)} — ${t.profileButton}`}>
          {t.profileGreeting(profile.name || profile.email)}
        </button>
      </div>
    );
  }

  return (
    <div className="cc-profile">
      <button type="button" className="cc-profile-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {profile ? t.profileGreeting(profile.name || profile.email) : t.profileButton}
      </button>
      {open && (
        <div className="cc-profile-panel" role="dialog" aria-label={t.profileTitle}>
          <strong style={{ color: "#F5F5F5" }}>{t.profileTitle}</strong>
          <label>
            {t.profileName}
            <input
              type="text"
              autoComplete="name"
              value={profile ? profile.name : name}
              onChange={(e) => (profile ? null : setName(e.target.value))}
              readOnly={!!profile}
            />
          </label>
          <label>
            {t.profileEmail}
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              value={profile ? profile.email : email}
              onChange={(e) => (profile ? null : setEmail(e.target.value))}
              readOnly={!!profile}
            />
          </label>
          <p className="cc-profile-note">{t.profileNote}</p>
          <div className="cc-profile-actions">
            {profile ? (
              <button type="button" className="cc-btn-ghost" onClick={() => { clear(); setOpen(false); }}>
                {t.profileRemove}
              </button>
            ) : (
              <button
                type="button"
                className="cc-btn-accent"
                disabled={!name.trim() && !email.trim()}
                onClick={() => { save({ name, email }); setName(""); setEmail(""); setOpen(false); }}
              >
                {t.profileSave}
              </button>
            )}
            <button type="button" className="cc-btn-ghost" onClick={() => setOpen(false)}>
              {t.profileCancel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const NAV_LINKS = [
  { href: "/send", key: "navSend" },
  { href: "/mail-guide", key: "navMailGuide" },
  { href: "/services", key: "navServices" },
  { href: "/faq", key: "navFaq" },
  { href: "/lookup", key: "navTrack" },
];

function SiteHeader() {
  const { t } = useSiteLocale();
  return (
    <>
      <SkipLink />
      <RouteAnnouncer />
      <header className="cc-site-header">
        <div className="cc-site-header-inner">
          <a className="cc-site-brand" href="/" aria-label="CareCard Express">
            <BrandMark size="sm" withWordmark={true} />
          </a>
          <nav className="cc-site-nav" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href}>
                {t[l.key]}
              </a>
            ))}
          </nav>
          <div className="cc-controls">
            <SiteLanguageSwitcher />
            <SiteThemeControls />
            <SiteAccentSwitcher />
            <A11yControls />
            <OptionalProfile />
          </div>
        </div>
      </header>
    </>
  );
}

const FOOTER_LINKS = [
  { href: "/send", key: "navSend" },
  { href: "/mail-guide", key: "navMailGuide" },
  { href: "/services", key: "navServices" },
  { href: "/faq", key: "navFaq" },
  { href: "/about", key: "navAbout" },
  { href: "/contact", key: "navContact" },
  { href: "/security", key: "navSecurity" },
  { href: "/lookup", key: "navTrack" },
];

function SiteFooter() {
  const { t } = useSiteLocale();
  return (
    <footer className="cc-site-footer">
      <div className="cc-site-footer-inner">
        <p className="cc-footer-disclaimer">{t.footerDisclaimer}</p>
        <nav className="cc-footer-links" aria-label="Footer">
          {FOOTER_LINKS.map((l) => (
            <a key={l.href} href={l.href}>
              {t[l.key]}
            </a>
          ))}
        </nav>
        <div className="cc-footer-meta">
          {t.footerTagline} · {t.footerRights}
        </div>
      </div>
    </footer>
  );
}

export {
  SiteHeader,
  SiteFooter,
  SkipLink,
  SiteLanguageSwitcher,
  LanguageSelect,
  SiteThemeControls,
  ThemeSwitch,
  SiteAccentSwitcher,
  A11yControls,
  OptionalProfile,
};
